import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

// Determina a URL base da API
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === "development") {
    return "/api";
  }

  const envUrl = window.REACT_APP_BACKEND_URL;
  if (!envUrl || envUrl === "") {
    return "/api";
  }

  if (envUrl.endsWith("/api")) {
    return envUrl;
  }

  return envUrl + "/api";
};

const API_BASE_URL = getApiBaseUrl();

const CertificateSuccessPage = () => {
  const { courseId, certId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);

  // Get certificate ID from URL params
  const certificateId = certId;
  const fromGroup = searchParams.get("fromGroup");

  // Get the back navigation URL based on fromGroup parameter
  const getBackUrl = () => {
    return fromGroup ? `/dashboard/group/${fromGroup}` : "/dashboard";
  };

  // Get course URL
  const getCourseUrl = () => {
    const baseUrl = `/course/${courseId}`;
    return fromGroup ? `${baseUrl}?fromGroup=${fromGroup}` : baseUrl;
  };

  useEffect(() => {
    const fetchCertificateData = async () => {
      try {
        if (certificateId) {
          const response = await fetch(`${API_BASE_URL}/certificates/${certificateId}`);
          if (response.ok) {
            const data = await response.json();
            setCertificateData(data);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching certificate:", error);
        setLoading(false);
      }
    };

    const fetchCourse = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/courses/${courseId}`);
        if (response.ok) {
          const courseData = await response.json();
          setCourse(courseData);
        }
      } catch (error) {
        console.error("Error fetching course:", error);
      }
    };

    if (certificateId) {
      fetchCertificateData();
    }
    if (courseId) {
      fetchCourse();
    }
  }, [certificateId, courseId]);

  const handleDownloadCertificate = async () => {
    try {
      const { downloadCertificate } = await import('../utils/certificateDownload');
      await downloadCertificate(certificateId);
    } catch (error) {
      console.error('Error downloading certificate:', error);
    }
  };

  const handleShareLinkedIn = () => {
    const courseTitle = course?.title || "Curso";
    const shareText = `Acabei de concluir o curso "${courseTitle}" na plataforma de treinamentos da Giro.Tech e obtive meu certificado! 🎓`;

    // LinkedIn sharing with pre-filled text
    const linkedInUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(shareText)}`;
    window.open(linkedInUrl, "_blank");
  };

  const handleBackToCourse = () => {
    navigate(getCourseUrl());
  };

  const handleBackToDashboard = () => {
    navigate(getBackUrl());
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando seu certificado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-8 text-center">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold mb-2">Parabéns!</h1>
            <p className="text-lg opacity-90">
              Você concluiu com sucesso o curso e obteve seu certificado
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Course Info */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {course?.title || "Curso Concluído"}
              </h2>
              <p className="text-gray-600 mb-4 whitespace-pre-line">{course?.description || ""}</p>
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                <span>Certificado ID: {certificateId}</span>
                <span>•</span>
                <span>{new Date().toLocaleDateString("pt-BR")}</span>
              </div>
            </div>

            {/* Certificate Data */}
            {certificateData && (
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Detalhes do Certificado
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Pontuação:</span>
                    <span className="ml-2 text-gray-900">{certificateData.score}%</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className="ml-2 text-green-600 font-medium">Aprovado</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Data de Conclusão:</span>
                    <span className="ml-2 text-gray-900">
                      {new Date(certificateData.generated_at).toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">ID de Validação:</span>
                    <span className="ml-2 text-gray-900 font-mono text-xs">
                      {certificateData.validation_id}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={handleDownloadCertificate}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span>Baixar Certificado</span>
              </button>

              <button
                onClick={handleShareLinkedIn}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                <span>Compartilhar no LinkedIn</span>
              </button>
            </div>

            {/* Navigation Buttons */}
            <div className="flex space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={handleBackToCourse}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Voltar ao Curso
              </button>
              <button
                onClick={handleBackToDashboard}
                className="flex-1 bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Ir para Dashboard
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-8 py-4 text-center">
            <div className="text-sm text-gray-500">
              Certificado válido e verificável via{" "}
              <a
                href="/verify-certificate"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Plataforma
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateSuccessPage;
